import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';

import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import {
    doc,
    getDoc,
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    setDoc,
    query,
    orderBy,
    where,
    getDocs
} from 'firebase/firestore';
import { app, auth, db } from './firebase';
import { churches } from './data/churches';

// Components
import NavButton from './components/NavButton';

// Already extracted pages
import { AdminPage } from './pages/AdminPage';
import MobileMenuPage from './pages/MobileMenuPage';
import { HonoursPage } from './pages/HonoursPage';
import { BeliefsPage } from './pages/BeliefsPage';
import LandingPage from './pages/LandingPage';

// Components (Lazy Loaded)
const FirstLoginModal = lazy(() => import('./components/FirstLoginModal'));
const OnboardingTour = lazy(() => import('./components/OnboardingTour'));

// Pages (Lazy Loaded)
const HomePage = lazy(() => import('./pages/HomePage'));
const MasterGuideEssentialsPage = lazy(() => import('./pages/MasterGuideEssentialsPage'));
const DevotionalsPage = lazy(() => import('./pages/DevotionalsPage'));
const StudyResourcesPage = lazy(() => import('./pages/StudyResourcesPage'));
const BibleTruthsPage = lazy(() => import('./pages/BibleTruthsPage'));
const ChurchHeritagePage = lazy(() => import('./pages/ChurchHeritagePage'));

// Main App Component
function App() {
    // ========================
    // STATE MANAGEMENT
    // ========================

    // Core app state
    const [currentPage, setCurrentPage] = useState('home');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Firebase state
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
    const [showOnboardingTour, setShowOnboardingTour] = useState(false);

    // Firestore listeners tracking
    const [firestoreListeners, setFirestoreListeners] = useState([]);

    // Authentication state
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState('signin'); // 'signin', 'signup', 'reset'
    const [authForm, setAuthForm] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        displayName: ''
    });

    // Data state
    const [memorizationTexts, setMemorizationTexts] = useState([]);
    const [memorizationSettings, setMemorizationSettings] = useState({ activeYears: [] });

    const [devotionals, setDevotionals] = useState([]);
    const [churchHistory, setChurchHistory] = useState('');

    // Progress tracking
    const [memorizationProgress, setMemorizationProgress] = useState({});
    const [honoursProgress, setHonoursProgress] = useState({});

    // ========================
    // PAGE NAVIGATION WITH SCROLL-TO-TOP
    // ========================
    const navigateToPage = (page) => {
        setCurrentPage(page);
        // Scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };




    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initializeFirebase = useCallback(async () => {
        try {
            setLoading(true);

            // Check for redirect result first
            let redirectResult;
            try {
                redirectResult = await getRedirectResult(auth);
            } catch (redirectError) {
                console.warn('Could not get redirect result (likely offline):', redirectError);
                redirectResult = null;
            }
            if (redirectResult) {
                const user = redirectResult.user;
                // Handle redirect result user creation
                try {
                    const userDocRef = doc(db, 'users', user.uid);
                    const userDoc = await getDoc(userDocRef);

                    if (!userDoc.exists()) {
                        // Create user profile for new Google users
                        await setDoc(userDocRef, {
                            name: user.displayName || user.email.split('@')[0],
                            email: user.email,
                            photoURL: user.photoURL,
                            createdAt: new Date(),
                            memorizationProgress: {},
                            honoursProgress: {}
                        });
                        setShowFirstLoginModal(true);
                    }
                    setShowAuthModal(false);
                } catch (firestoreError) {
                    console.warn('Could not access Firestore (likely offline):', firestoreError);
                    // Still proceed with auth state setup even if Firestore is offline
                    setShowAuthModal(false);
                }
            }

            // Setup auth state listener
            onAuthStateChanged(auth, async (firebaseUser) => {
                try {
                    if (firebaseUser) {
                        setUser(firebaseUser);
                        try {
                            await updateUserLoginInfo(firebaseUser); // Update login info and ensure email is stored
                            await loadUserProfile(firebaseUser.uid);
                            await loadAppData(firebaseUser.uid);
                        } catch (loadError) {
                            console.warn('Could not load user data (likely offline):', loadError);
                            // Still set the user even if we can't load their data
                        }
                    } else {
                        // Clean up listeners when user signs out
                        firestoreListeners.forEach(unsubscribe => {
                            if (typeof unsubscribe === 'function') {
                                unsubscribe();
                            }
                        });
                        setFirestoreListeners([]);

                        setUser(null);
                        setUserProfile(null);
                        setMemorizationTexts([]);
                        setDevotionals([]);
                        setChurchHistory('');
                        setMemorizationProgress({});
                        setHonoursProgress({});
                    }
                } catch (authError) {
                    console.warn('Auth state change error:', authError);
                } finally {
                    setLoading(false);
                }
            });

            // Attempt authentication
            // Don't automatically show auth modal - let buttons handle it

        } catch (error) {
            console.error('Firebase initialization error:', error);
            setError('Failed to initialize app');
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, db, firestoreListeners]);

    useEffect(() => {
        // Initialize Firebase when app starts - only run once
        initializeFirebase();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    const authenticateUser = async () => {
        setShowAuthModal(true);
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        try {
            setError(null);
            setLoading(true);

            if (authMode === 'signup') {
                if (authForm.password !== authForm.confirmPassword) {
                    setError('Passwords do not match');
                    setLoading(false);
                    return;
                }
                if (authForm.password.length < 6) {
                    setError('Password must be at least 6 characters');
                    setLoading(false);
                    return;
                }

                const userCredential = await createUserWithEmailAndPassword(auth, authForm.email, authForm.password);

                // Create user profile
                const userDocRef = doc(db, 'users', userCredential.user.uid);
                await setDoc(userDocRef, {
                    name: authForm.displayName || authForm.email.split('@')[0],
                    email: authForm.email,
                    createdAt: new Date(),
                    memorizationProgress: {},
                    honoursProgress: {}
                });

                setShowAuthModal(false);
                setShowFirstLoginModal(true);

            } else if (authMode === 'signin') {
                await signInWithEmailAndPassword(auth, authForm.email, authForm.password);
                setShowAuthModal(false);

            } else if (authMode === 'reset') {
                await sendPasswordResetEmail(auth, authForm.email);
                setError(null);
                alert('Password reset email sent! Check your inbox.');
                setAuthMode('signin');
            }

            setAuthForm({ email: '', password: '', confirmPassword: '', displayName: '' });
            setLoading(false);

        } catch (error) {
            console.error('Authentication error:', error);
            setLoading(false);

            switch (error.code) {
                case 'auth/user-not-found':
                    setError('No account found with this email address');
                    break;
                case 'auth/wrong-password':
                    setError('Incorrect password');
                    break;
                case 'auth/email-already-in-use':
                    setError('An account with this email already exists');
                    break;
                case 'auth/weak-password':
                    setError('Password is too weak');
                    break;
                case 'auth/invalid-email':
                    setError('Invalid email address');
                    break;
                default:
                    setError(error.message);
            }
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            setError(null);
            setLoading(true);

            const provider = new GoogleAuthProvider();
            // Add additional scopes if needed
            provider.addScope('email');
            provider.addScope('profile');

            // Configure provider to avoid COOP issues
            provider.setCustomParameters({
                prompt: 'select_account'
            });

            let result;
            try {
                // Try popup first
                result = await signInWithPopup(auth, provider);
            } catch (popupError) {
                console.log('Popup blocked or failed, trying redirect:', popupError);
                if (popupError.code === 'auth/popup-blocked' ||
                    popupError.code === 'auth/popup-closed-by-user' ||
                    popupError.code === 'auth/unauthorized-domain' ||
                    popupError.message?.includes('Cross-Origin-Opener-Policy') ||
                    popupError.message?.includes('window.closed')) {
                    // Fall back to redirect
                    setLoading(false); // Stop loading before redirect
                    await signInWithRedirect(auth, provider);
                    return; // Exit here as redirect will reload the page
                } else {
                    throw popupError;
                }
            }

            const googleUser = result.user;

            // Check if this is a new user and create profile if needed
            try {
                const userDocRef = doc(db, 'users', googleUser.uid);
                const userDoc = await getDoc(userDocRef);

                if (!userDoc.exists()) {
                    // Create user profile for new Google users
                    await setDoc(userDocRef, {
                        name: googleUser.displayName || googleUser.email.split('@')[0],
                        email: googleUser.email,
                        photoURL: googleUser.photoURL,
                        createdAt: new Date(),
                        memorizationProgress: {},
                        honoursProgress: {}
                    });
                    setShowFirstLoginModal(true);
                }
            } catch (firestoreError) {
                console.warn('Could not create/check user profile (likely offline):', firestoreError);
                // Still proceed with authentication
            }

            setShowAuthModal(false);
            setLoading(false);

        } catch (error) {
            console.error('Google Sign-In error:', error);
            setLoading(false);

            switch (error.code) {
                case 'auth/popup-closed-by-user':
                    setError('Sign-in was cancelled');
                    break;
                case 'auth/popup-blocked':
                    setError('Popup was blocked by browser. Please allow popups and try again.');
                    break;
                case 'auth/cancelled-popup-request':
                    // User closed popup, no error needed
                    break;
                case 'auth/configuration-not-found':
                    setError('Google Sign-In is not configured. Please enable Google authentication in Firebase Console.');
                    break;
                case 'auth/unauthorized-domain':
                    setError('This domain is not authorized. Please add localhost to authorized domains in Firebase Console.');
                    break;
                default:
                    // Check for Cross-Origin-Opener-Policy errors
                    if (error.message?.includes('Cross-Origin-Opener-Policy') ||
                        error.message?.includes('window.closed')) {
                        setError('Browser security policy blocked sign-in. Trying alternative method...');
                        // Try redirect as fallback
                        try {
                            const provider = new GoogleAuthProvider();
                            await signInWithRedirect(auth, provider);
                            return;
                        } catch (redirectError) {
                            setError('Sign-in failed. Please try again or contact support.');
                        }
                    } else {
                        setError(`Sign-in failed: ${error.message}. Please make sure Google authentication is enabled in Firebase Console.`);
                    }
            }
        }
    };

    // ========================
    // DATA LOADING FUNCTIONS
    // ========================

    const loadUserProfile = async (uid) => {
        try {
            const userDocRef = doc(db, 'users', uid);

            const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const profile = docSnapshot.data();
                    setUserProfile(profile);
                    setMemorizationProgress(profile.memorizationProgress || {});
                    setHonoursProgress(profile.honoursProgress || {});

                    if (profile.isFirstLogin) {
                        setShowFirstLoginModal(true);
                    } else if (profile.tourSeen !== true) {
                        // If they haven't seen the tour (and it's not their first login right now), show it
                        // Optional: You might only want to show it immediately after first login modal closes.
                        // For now, let's trigger it if the flag is missing, effectively "onboarding" existing users too.
                        setShowOnboardingTour(true);
                    }
                } else {
                    // Create new user profile
                    setShowFirstLoginModal(true);
                }
            });

            // Track this listener for cleanup
            setFirestoreListeners(prev => [...prev, unsubscribe]);
        } catch (error) {
            console.error('Error loading user profile:', error);
        }
    };

    // Update user's last login time and ensure email is stored
    const updateUserLoginInfo = async (firebaseUser) => {
        try {
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            await updateDoc(userDocRef, {
                lastLogin: new Date(),
                email: firebaseUser.email, // Ensure email is always stored/updated
            });
        } catch (error) {
            console.warn('Could not update login info (likely offline):', error);
            // Don't block authentication if Firestore is unavailable
        }
    };

    const loadAppData = async (uid) => {
        try {
            const appId = process.env.REACT_APP_MASTER_GUIDE_APP_ID || window.__app_id || 'masterguide-2a719';

            // Load memorization texts
            const memTextsRef = collection(db, `artifacts/${appId}/public/data/memorizationTexts`);
            const unsubscribeMemTexts = onSnapshot(memTextsRef, (snapshot) => {
                const texts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setMemorizationTexts(texts);
            });

            // Load Settings
            const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'memorization');
            const unsubscribeSettings = onSnapshot(settingsRef, (docSnapshot) => {
                if (docSnapshot.exists()) {
                    setMemorizationSettings(docSnapshot.data());
                } else {
                    setMemorizationSettings({ activeYears: [] });
                }
            });

            // Load churches - Using local data instead of Firestore for comprehensive Caribbean coverage
            // const churchesRef = collection(db, `artifacts/${appId}/public/data/churches`);
            // onSnapshot(churchesRef, (snapshot) => {
            //     const churchesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            //     setChurches(churchesData);
            // });

            // Load church history
            const historyRef = doc(db, `artifacts/${appId}/public/data/churchHistory`, 'history');
            const unsubscribeHistory = onSnapshot(historyRef, (docSnapshot) => {
                if (docSnapshot.exists()) {
                    setChurchHistory(docSnapshot.data().content || '');
                }
            });

            // Load user devotionals - more efficient with predictable document IDs
            console.log('ðŸ” Journal Loading Debug: Setting up devotionals query for uid:', uid);
            const devotionalsRef = collection(db, 'journals');
            const devotionalsQuery = query(
                devotionalsRef,
                where('userId', '==', uid),
                orderBy('createdAt', 'desc')
            );
            console.log('ðŸ” Journal Loading Debug: Query created, setting up listener...');
            const unsubscribeDevotionals = onSnapshot(devotionalsQuery, (snapshot) => {
                console.log('ðŸ” Journal Loading Debug: Snapshot received');
                console.log('ðŸ” Journal Loading Debug: Snapshot size:', snapshot.size);
                console.log('ðŸ” Journal Loading Debug: Snapshot docs:', snapshot.docs);
                const devotionalsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log('ðŸ” Journal Loading Debug: Processed devotionals data:', devotionalsData);
                setDevotionals(devotionalsData);
            }, (error) => {
                console.error('ðŸš¨ Journal Loading Error:', error);
                console.error('ðŸš¨ Error code:', error.code);
                console.error('ðŸš¨ Error message:', error.message);
            });

            // Track all listeners for cleanup
            setFirestoreListeners(prev => [
                ...prev,
                unsubscribeMemTexts,
                unsubscribeSettings,
                unsubscribeHistory,
                unsubscribeDevotionals
            ]);

        } catch (error) {
            console.error('Error loading app data:', error);
        }
    };    // ========================
    // AUTHENTICATION HANDLERS
    // ========================

    const handleFirstLogin = async (profileData) => {
        try {
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, {
                uid: user.uid,
                name: profileData.name,
                email: user.email, // Ensure email from Firebase Auth is stored
                island: profileData.island,
                church: profileData.church,
                isFirstLogin: false,
                memorizationProgress: {},
                honoursProgress: {},
                createdAt: new Date()
            }, { merge: true }); // Use merge to preserve any existing fields

            setShowFirstLoginModal(false);
            setShowOnboardingTour(true); // Start tour immediately after profile setup
        } catch (error) {
            console.error('Error creating user profile:', error);
            setError('Failed to create profile');
        }
    };

    const handleLogout = async () => {
        try {
            // Clean up all Firestore listeners before signing out
            firestoreListeners.forEach(unsubscribe => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            });
            setFirestoreListeners([]);

            // Clear state
            setMemorizationTexts([]);
            setDevotionals([]);
            setChurchHistory('');
            setMemorizationProgress({});
            setHonoursProgress({});

            await signOut(auth);
            navigateToPage('home');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // ========================
    // UTILITY FUNCTIONS
    // ========================



    const isAdmin = () => {
        const adminUserId = process.env.REACT_APP_ADMIN_USER_ID || 'adminUser123';
        return user?.uid === adminUserId;
    };



    // ========================
    // PROGRESS TRACKING
    // ========================

    const updateMemorizationProgress = async (textId, completed) => {
        try {
            const newProgress = { ...memorizationProgress, [textId]: completed };
            setMemorizationProgress(newProgress);

            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, { memorizationProgress: newProgress });
        } catch (error) {
            console.error('Error updating memorization progress:', error);
        }
    };

    const updateHonoursProgress = async (honourId, itemId, completed) => {
        try {
            const newProgress = {
                ...honoursProgress,
                [honourId]: {
                    ...honoursProgress[honourId],
                    [itemId]: completed
                }
            };
            setHonoursProgress(newProgress);

            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, { honoursProgress: newProgress });
        } catch (error) {
            console.error('Error updating honours progress:', error);
        }
    };




    // ========================
    // RENDER FUNCTIONS
    // ========================

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                        <span className="text-white font-bold text-3xl">MG</span>
                    </div>
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading Master Guide Resources</h3>
                    <p className="text-gray-600">Preparing your leadership development platform...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                        <span className="text-white text-4xl">⚠️</span>
                    </div>
                    <h2 className="text-2xl font-bold text-red-800 mb-4">Something went wrong</h2>
                    <div className="bg-red-100 border border-red-200 rounded-xl p-4 mb-6">
                        <p className="text-red-700">{error}</p>
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-semibold px-8 py-3 rounded-xl transition duration-300 transform hover:scale-105"
                    >
                        Reload Application
                    </button>
                </div>
            </div>
        );
    }

    const handleTourComplete = async () => {
        setShowOnboardingTour(false);
        if (user) {
            try {
                const userDocRef = doc(db, 'users', user.uid);
                await updateDoc(userDocRef, { tourSeen: true });
            } catch (error) {
                console.error('Error updating tour status:', error);
            }
        }
    };

    // Authentication modal - check this FIRST before any other rendering
    if (showAuthModal) {
        return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-md w-full mx-4 shadow-2xl">
                    {/* Header */}
                    <div className="text-center p-8 pb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <span className="text-white font-bold text-2xl">MG</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {authMode === 'signin' && 'Welcome Back'}
                            {authMode === 'signup' && 'Join Master Guide'}
                            {authMode === 'reset' && 'Reset Password'}
                        </h2>
                        <p className="text-gray-600">
                            {authMode === 'signin' && 'Sign in to continue your journey'}
                            {authMode === 'signup' && 'Start your leadership development'}
                            {authMode === 'reset' && 'Enter your email to reset password'}
                        </p>
                    </div>

                    <div className="px-8 pb-8">
                        <form onSubmit={handleAuth} className="space-y-4">
                            {authMode === 'signup' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Display Name
                                    </label>
                                    <input
                                        type="text"
                                        autoComplete="name"
                                        value={authForm.displayName}
                                        onChange={(e) => setAuthForm({ ...authForm, displayName: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-300"
                                        placeholder="Your name"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={authForm.email}
                                    onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-300"
                                    placeholder="your.email@example.com"
                                />
                            </div>

                            {authMode !== 'reset' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        required
                                        autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                                        value={authForm.password}
                                        onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-300"
                                        placeholder="Password"
                                        minLength={authMode === 'signup' ? 6 : undefined}
                                    />
                                </div>
                            )}

                            {authMode === 'signup' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm Password
                                    </label>
                                    <input
                                        type="password"
                                        required
                                        autoComplete="new-password"
                                        value={authForm.confirmPassword}
                                        onChange={(e) => setAuthForm({ ...authForm, confirmPassword: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-300"
                                        placeholder="Confirm password"
                                    />
                                </div>
                            )}

                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                                    <div className="flex">
                                        <span className="text-red-500 mr-2">⚠️</span>
                                        <span>{error}</span>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-xl transition duration-300"
                                >
                                    {loading ? 'Please wait...' : (
                                        authMode === 'signin' ? 'Sign In' :
                                            authMode === 'signup' ? 'Create Account' :
                                                'Send Reset Email'
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAuthModal(false);
                                        setError(null);
                                        setAuthForm({ email: '', password: '', confirmPassword: '', displayName: '' });
                                    }}
                                    className="px-6 py-3 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition duration-300"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>

                        {authMode !== 'reset' && (
                            <>
                                <div className="mt-6 mb-6 text-center">
                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-300"></div>
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="px-2 bg-white text-gray-500">Or continue with</span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleGoogleSignIn}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 disabled:bg-gray-100 transition duration-300"
                                >
                                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    {loading ? 'Signing in...' : 'Continue with Google'}
                                </button>
                            </>
                        )}

                        <div className="mt-6 text-center text-sm">
                            {authMode === 'signin' && (
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setAuthMode('reset')}
                                        className="text-indigo-600 hover:text-indigo-800 font-medium"
                                    >
                                        Forgot your password?
                                    </button>
                                    <div>
                                        <span className="text-gray-500">Don't have an account? </span>
                                        <button
                                            onClick={() => setAuthMode('signup')}
                                            className="text-indigo-600 hover:text-indigo-800 font-medium"
                                        >
                                            Sign up
                                        </button>
                                    </div>
                                </div>
                            )}
                            {authMode === 'signup' && (
                                <div>
                                    <span className="text-gray-500">Already have an account? </span>
                                    <button
                                        onClick={() => setAuthMode('signin')}
                                        className="text-indigo-600 hover:text-indigo-800 font-medium"
                                    >
                                        Sign in
                                    </button>
                                </div>
                            )}
                            {authMode === 'reset' && (
                                <button
                                    onClick={() => setAuthMode('signin')}
                                    className="text-indigo-600 hover:text-indigo-800 font-medium"
                                >
                                    Back to sign in
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Public landing page about Master Guiding
    if (!user) {
        return (
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
                <LandingPage
                    onSignIn={() => {
                        setAuthMode('signin');
                        setShowAuthModal(true);
                    }}
                    onSignUp={() => {
                        setAuthMode('signup');
                        setShowAuthModal(true);
                    }}
                    onGoogleSignIn={handleGoogleSignIn}
                />
            </Suspense>
        );
    }

    // First login modal
    if (showFirstLoginModal) {
        return (
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading setup...</div>}>
                <FirstLoginModal onComplete={handleFirstLogin} churches={churches} />
            </Suspense>
        );
    }

    // Main authenticated app
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
            {/* Navigation */}
            <nav className="bg-white/95 backdrop-blur-sm shadow-lg border-b border-indigo-100 sticky top-0 z-50">
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center py-4">
                        {/* Logo - Clickable to go home */}
                        <button
                            onClick={() => navigateToPage('home')}
                            className="flex items-center space-x-3 hover:opacity-80 transition duration-300"
                        >
                            <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-bold text-xl">MG</span>
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                    Master Guide
                                </h1>
                                <p className="text-sm text-gray-500">Pathfinder Leadership Platform</p>
                            </div>
                        </button>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex space-x-2">
                            <NavButton page="home" currentPage={currentPage} onClick={navigateToPage}>
                                <span className="flex items-center space-x-2">
                                    <span>🏠</span>
                                    <span>Home</span>
                                </span>
                            </NavButton>
                            <NavButton page="essentials" currentPage={currentPage} onClick={navigateToPage}>
                                <span className="flex items-center space-x-2">
                                    <span>📋</span>
                                    <span>MG Essentials</span>
                                </span>
                            </NavButton>
                            <NavButton page="devotionals" currentPage={currentPage} onClick={navigateToPage}>
                                <span className="flex items-center space-x-2">
                                    <span>📖</span>
                                    <span>Journal</span>
                                </span>
                            </NavButton>
                            <NavButton page="study-resources" currentPage={currentPage} onClick={navigateToPage}>
                                <span className="flex items-center space-x-2">
                                    <span>📚</span>
                                    <span>Study Resources</span>
                                </span>
                            </NavButton>
                            <NavButton page="honours" currentPage={currentPage} onClick={navigateToPage}>
                                <span className="flex items-center space-x-2">
                                    <span>🎯</span>
                                    <span>Honours</span>
                                </span>
                            </NavButton>
                            {isAdmin() && (
                                <NavButton page="admin" currentPage={currentPage} onClick={navigateToPage}>
                                    <span className="flex items-center space-x-2">
                                        <span>⚙️</span>
                                        <span>Admin</span>
                                    </span>
                                </NavButton>
                            )}
                        </div>

                        {/* User Info and Actions */}
                        <div className="flex items-center space-x-4">
                            {/* Welcome message moved to body */}
                            {userProfile && (
                                <div className="hidden md:flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center">
                                        <span className="text-white font-semibold text-sm">
                                            {userProfile.name?.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="text-sm">
                                        <div className="font-medium text-gray-900">{userProfile.name}</div>
                                        <div className="text-gray-500">{userProfile.church}</div>
                                    </div>
                                </div>
                            )}

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => navigateToPage('menu')}
                                className="lg:hidden bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition duration-300"
                            >
                                ☰ Menu
                            </button>

                            <button
                                onClick={handleLogout}
                                className="text-gray-600 hover:text-gray-800 px-4 py-2 rounded-xl hover:bg-gray-100 transition duration-300"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="container mx-auto px-4 py-8">
                <Suspense fallback={
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                        <p className="text-gray-500 font-medium">Loading page...</p>
                    </div>
                }>
                    {currentPage === 'home' && <HomePage userProfile={userProfile} navigateToPage={navigateToPage} memorizationProgress={memorizationProgress} memorizationTexts={memorizationTexts} devotionals={devotionals} honoursProgress={honoursProgress} />}
                    {currentPage === 'essentials' && <MasterGuideEssentialsPage navigateToPage={navigateToPage} />}
                    {currentPage === 'info' && <MasterGuideEssentialsPage navigateToPage={navigateToPage} />} {/* Backward compatibility */}
                    {currentPage === 'devotionals' && <DevotionalsPage user={user} userProfile={userProfile} devotionals={devotionals} db={db} navigateToPage={navigateToPage} />}
                    {currentPage === 'study-resources' && <StudyResourcesPage navigateToPage={navigateToPage} />}
                    {currentPage === 'bible-truths' && <BibleTruthsPage navigateToPage={navigateToPage} memorizationProgress={memorizationProgress} updateMemorizationProgress={updateMemorizationProgress} />}
                    {currentPage === 'beliefs' && <BeliefsPage />}
                    {currentPage === 'church-heritage' && <ChurchHeritagePage navigateToPage={navigateToPage} />}
                    {currentPage === 'honours' && (
                        <HonoursPage
                            honoursProgress={honoursProgress}
                            updateHonoursProgress={updateHonoursProgress}
                            navigateToPage={navigateToPage}
                        />
                    )}
                    {currentPage === 'admin' && isAdmin() && <AdminPage user={user} memorizationTexts={memorizationTexts} memorizationSettings={memorizationSettings} navigateToPage={navigateToPage} />}
                    {currentPage === 'menu' && <MobileMenuPage user={user} userProfile={userProfile} navigateToPage={navigateToPage} currentPage={currentPage} handleLogout={handleLogout} memorizationProgress={memorizationProgress} memorizationTexts={memorizationTexts} devotionals={devotionals} />}
                </Suspense>
            </main>

            {/* Onboarding Tour */}
            <Suspense fallback={null}>
                <OnboardingTour
                    show={showOnboardingTour}
                    onComplete={handleTourComplete}
                />
            </Suspense>
        </div>
    );

    // Note: Individual page components have been extracted to the src/pages/ directory
    // and are now lazy-loaded for better performance and smaller initial bundle size.

}

export default App;
